import { Component, DoCheck, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { TsInputComponent } from 'tsui';

import { FormatNumberPipe } from '@shared/pipes/format-number.pipe';
import { FormFieldComponent } from '../form-field.types';

const REDACTED_PLACEHOLDER = 'REDACTED';

@Component({
  templateUrl: './percent-form-field.component.html',
  styleUrls: ['./../form-field.component.scss'],
})
export class PercentFormFieldComponent extends FormFieldComponent implements OnInit, DoCheck {
  @ViewChild(TsInputComponent) private readonly tsInput?: TsInputComponent;

  private valueMemento: string;
  private precision: number = 0;

  public editing: boolean = false;
  public valueControl = new UntypedFormControl();
  // Hidden values are never editable, so they get their own control: keeping the placeholder out of
  // `valueControl` stops it from reaching the save payload.
  public readonly redactedControl = new FormControl({ value: REDACTED_PLACEHOLDER, disabled: true });
  // Overrides the inherited Material-specific selector: ts-input has no .mat-form-field-flex to target.
  public readonly commonTargetStylingClass = '.ts-input';

  // Duck-typed contract read by form-field.component.ts to avoid clobbering an in-progress edit.
  get inputControl(): ElementRef<HTMLInputElement> | undefined {
    return this.tsInput?.inputElement();
  }

  ngOnInit(): void {
    super.ngOnInit();

    if (this.fieldDef.meta && this.fieldDef.meta.scale) {
      this.precision = this.fieldDef.meta.scale;
    }

    this.updateDisabledState();

    this.setValues();
  }

  ngDoCheck(): void {
    this.updateDisabledState();
  }

  updateChangedModel(model: any): void {
    this.model = model;
    this.setValues();
  }

  edit(): void {
    if (!this.editing && !this.isReadonly) {
      this.editing = true;
      setTimeout(() => {
        this.inputControl?.nativeElement.focus();
      }, 300);
    }
  }

  onBlur(): void {
    this.editing = false;

    const rawValue = this.valueControl.value;
    const value = rawValue ? String(Number(rawValue)) : null;

    if (this.valueMemento === value) {
      return;
    }

    this.model[this.fieldDef.name] = value;
    this.update.emit({ [this.fieldDef.name]: value });
  }

  private setValues(): void {
    const value = new FormatNumberPipe().transform(this.model[this.fieldDef.name], this.precision, this.fieldDef);
    this.valueControl.setValue(value);
    this.valueMemento = this.fieldDef.meta?.value_hidden ? REDACTED_PLACEHOLDER : value;
  }

  private updateDisabledState(): void {
    if (this.isDisabled === this.valueControl.disabled) {
      return;
    }

    if (this.isDisabled) {
      this.valueControl.disable({ emitEvent: false });
    } else {
      this.valueControl.enable({ emitEvent: false });
    }
  }
}
